import React, { useState, useEffect } from 'react';
import API_CONFIG, { buildApiUrl } from '../config/api';
import authService from '../services/auth-service';
import { TylerState } from '../utils/TylerState';
import AdminPanel from './AdminPanel';
import AdminTestingPanel from './AdminTestingPanel';
import '../styles/modal.css';

interface AnalyticsData {
  total_events: number;
  recent_events_count: number;
  calculator_usage: { [key: string]: number };
  device_distribution: { [key: string]: number };
  system_status: string;
}

interface UserSummary {
  id: number;
  username: string;
  email: string;
  date_joined: string;
  status: string;
  tier: string;
  subscription_status: string;
  subscription_end_date?: string;
  payment_amount?: number;
  payment_date?: string;
  is_active: boolean;
  is_verified: boolean;
  auth_method: string; // 'email' or 'google'
  google_id?: string;
}

interface UpdateUserStatusRequest {
  user_id: number;
  tier: string;
  subscription_status: string;
  subscription_end_date?: string;
}

interface SavedScenario {
  id: number;
  calculator: string;
  user_email: string;
  user_tier: string;
  scenario_name: string;
  created_at: string;
  updated_at: string;
}

const AnalyticsDashboard: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [scenarios, setScenarios] = useState<SavedScenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'analytics' | 'users' | 'regulatory' | 'admin'>('analytics');
  const [editingUser, setEditingUser] = useState<UserSummary | null>(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [dataLoadedCount, setDataLoadedCount] = useState(0); // Track how many data sources are loaded

  // Access control - only allow configured admin email (default: tylerhargrave@gmail.com)
  const adminEmail = process.env.REACT_APP_ANALYTICS_ADMIN_EMAIL || 'tylerhargrave@gmail.com';
  const [user, setUser] = useState<any>(null);

  // Effect to set loading to false when all data sources are loaded
  useEffect(() => {
    if (dataLoadedCount >= 3) {
      setLoading(false);
    }
  }, [dataLoadedCount]);

  // Removed debug logging for editingUser state changes

  useEffect(() => {
    const checkAccess = async () => {
      try {
        // Get current user from TylerState (global state)
        const currentUser = TylerState.user;
        console.log('Current user from TylerState:', currentUser);

        if (!currentUser || !currentUser.email) {
          setError('Please log in to access analytics');
          setLoading(false);
          return;
        }

        if (currentUser.email.toLowerCase() === adminEmail.toLowerCase()) {
          setUser(currentUser);
          fetchAnalyticsData();
          fetchUsers();
          fetchScenarios();
        } else {
          setError(`Access Denied - Admin access required (${adminEmail})`);
          setLoading(false);
        }
      } catch (err) {
        console.error('Auth check failed:', err);
        setError('Authentication failed - please log in');
        setLoading(false);
      }
    };

    checkAccess();
  }, [adminEmail, TylerState.user]);

  const fetchAnalyticsData = async () => {
    try {
      // Use the centralized API configuration with authentication headers
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.ANALYTICS_DASHBOARD), {
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeaders()
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please check your Gmail login credentials.');
        } else if (response.status === 403) {
          throw new Error('Access denied. You do not have permission to view analytics.');
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }
      
      const data = await response.json();
      
      if (data.status === 'success') {
        setAnalyticsData(data.data);
        setDataLoadedCount(prev => prev + 1); // Increment loaded count
      } else {
        throw new Error(data.message || 'Failed to fetch analytics data');
      }
    } catch (err) {
      console.error('Error in fetchAnalyticsData:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
      setDataLoadedCount(prev => prev + 1); // Increment even on error to prevent infinite loading
    }
  };

  const fetchUsers = async () => {
    try {
      // Use the centralized API configuration with authentication headers
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.ADMIN_USERS), {
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeaders()
        }
      });

      if (!response.ok) {
        // Don't fail the whole dashboard if users endpoint fails
        console.log('Failed to fetch users:', response.status);
        if (response.status === 401 || response.status === 403) {
          console.log('User management requires admin privileges - skipping');
        }
        setDataLoadedCount(prev => prev + 1);
        return;
      }

      const data = await response.json();

      // 🔍 LOG ENTIRE USERS TABLE
      console.log('═══════════════════════════════════════════════════════════');
      console.log('📊 FULL USERS TABLE DATA:');
      console.log('═══════════════════════════════════════════════════════════');
      console.log('Total users:', data.length);
      console.log('\n');
      data.forEach((user: UserSummary, index: number) => {
        console.log(`--- USER ${index + 1} ---`);
        console.log('ID:', user.id);
        console.log('Username:', user.username);
        console.log('Email:', user.email);
        console.log('Tier:', user.tier);
        console.log('Status:', user.status);
        console.log('Subscription Status:', user.subscription_status);
        console.log('Subscription End Date:', user.subscription_end_date);
        console.log('Active:', user.is_active);
        console.log('Verified:', user.is_verified);
        console.log('Auth Method:', user.auth_method);
        console.log('Date Joined:', user.date_joined);
        console.log('Payment Amount:', user.payment_amount);
        console.log('Payment Date:', user.payment_date);
        console.log('\n');
      });
      console.log('═══════════════════════════════════════════════════════════');
      console.table(data);
      console.log('═══════════════════════════════════════════════════════════');

      setUsers(data);
      setDataLoadedCount(prev => prev + 1); // Increment loaded count
    } catch (err) {
      console.error('Error in fetchUsers:', err);
      // Don't set error state - just log it and increment counter
      console.log('User management features will be unavailable');
      setDataLoadedCount(prev => prev + 1); // Increment even on error to prevent infinite loading
    }
  };

  const fetchScenarios = async () => {
    try {
      const response = await fetch(buildApiUrl('/admin/scenarios'), {
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeaders()
        }
      });

      if (!response.ok) {
        console.log('Failed to fetch scenarios:', response.status);
        setDataLoadedCount(prev => prev + 1);
        return;
      }

      const data = await response.json();

      if (data.scenarios) {
        setScenarios(data.scenarios);
      }
      setDataLoadedCount(prev => prev + 1);
    } catch (err) {
      console.error('Error in fetchScenarios:', err);
      setDataLoadedCount(prev => prev + 1);
    }
  };

  const updateUserStatus = async (request: UpdateUserStatusRequest) => {
    try {
      setUpdateLoading(true);

      const endpoint = buildApiUrl(API_CONFIG.ENDPOINTS.ADMIN_USER_STATUS(request.user_id));
      const authHeaders = authService.getAuthHeaders();

      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        // Update the local users state
        setUsers(prevUsers =>
          prevUsers.map(user =>
            user.id === request.user_id
              ? {
                  ...user,
                  tier: request.tier,
                  subscription_status: request.subscription_status,
                  subscription_end_date: request.subscription_end_date
                }
              : user
          )
        );
        setEditingUser(null);

        // Refresh users from server to ensure data consistency
        fetchUsers();

      } else {
        throw new Error(data.message || 'Failed to update user status');
      }
    } catch (err) {
      console.error('Error in updateUserStatus:', err);
      alert(`Failed to update user status: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setUpdateLoading(false);
    }
  };

  const formatTimestamp = (timestamp: string) => new Date(timestamp).toLocaleString();
  const formatDate = (date: string) => new Date(date).toLocaleDateString();
  const formatCurrency = (amount?: number) => amount ? `$${amount.toFixed(2)} CAD` : 'N/A';

  // Show access denied message
  if (error === 'Access Denied') {
    return (
      <div className="analytics-dashboard">
        <div className="access-denied">
          <h1>🚫 Access Denied</h1>
          <p>This analytics dashboard is restricted to authorized users only.</p>
          <p>If you believe you should have access, please contact the administrator.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="analytics-dashboard">
        <div className="loading">Loading analytics data...</div>
      </div>  
    );
  }

  if (error && error !== 'Access Denied') {
    return (
      <div className="analytics-dashboard">
        <div className="error">Error: {error}</div>
        <button onClick={fetchAnalyticsData} className="retry-btn">Retry</button>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="analytics-dashboard">
        <div className="no-data">No analytics data available</div>
      </div>
    );
  }

  return (
    <div className="analytics-dashboard">
      <h1>📊 Larry's Help Analytics Dashboard</h1>
      <p className="generated-time">
        Generated: {new Date().toLocaleString()} (Local Time)
      </p>
      <p className="user-info">
        Access granted to: {TylerState.user?.email}
      </p>

      {/* Tab Navigation */}
      <div className="dashboard-tabs">
        <button 
          className={`tab-button ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          📊 Analytics
        </button>
        <button 
          className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          👥 User Management
        </button>
        <button 
          className={`tab-button ${activeTab === 'regulatory' ? 'active' : ''}`}
          onClick={() => setActiveTab('regulatory')}
        >
          📜 Regulatory Updates
        </button>
        <button 
          className={`tab-button ${activeTab === 'admin' ? 'active' : ''}`}
          onClick={() => setActiveTab('admin')}
        >
          ⚙️ Admin Panel
        </button>
      </div>

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <>
          <div className="stats-grid">
            <div className="analytics-stat-card">
              <h3>📈 Overview</h3>
              <div className="stat-item">
                <span>Total Events:</span>
                <span className="stat-value">{analyticsData?.total_events?.toLocaleString() || '0'}</span>
              </div>
              <div className="stat-item">
                <span>Recent Events:</span>
                <span className="stat-value">{analyticsData?.recent_events_count?.toLocaleString() || '0'}</span>
              </div>
            </div>

            <div className="analytics-stat-card">
              <h3>📊 System Status</h3>
              <div className="stat-item">
                <span>Status:</span>
                <span className="stat-value">{analyticsData?.system_status || 'Unknown'}</span>
              </div>
            </div>

            <div className="analytics-stat-card">
              <h3>📱 Device Types</h3>
              {analyticsData?.device_distribution && Object.entries(analyticsData.device_distribution).map(([device, count]) => (
                <div key={device} className="stat-item">
                  <span>{device}:</span>
                  <span className="stat-value">{count.toLocaleString()}</span>
                </div>
              )) || <div className="stat-item">No device data</div>}
            </div>

            <div className="analytics-stat-card">
              <h3>🧮 Calculator Usage</h3>
              {analyticsData?.calculator_usage && Object.entries(analyticsData.calculator_usage).map(([calculator, count]) => {
                const name = calculator.replace(/_/g, ' ');
                return (
                  <div key={calculator} className="stat-item">
                    <span>{name}:</span>
                    <span className="stat-value">{count.toLocaleString()}</span>
                  </div>
                );
              }) || <div className="stat-item">No calculator data</div>}
            </div>
          </div>

          {/* Saved Scenarios Section */}
          <div className="analytics-stat-card" style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3>💾 All Saved Scenarios ({scenarios.length})</h3>
              <button
                onClick={fetchScenarios}
                style={{
                  padding: '8px 16px',
                  background: '#1890ff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Refresh
              </button>
            </div>

            {scenarios.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                No scenarios found
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ padding: '12px 8px', textAlign: 'left' }}>Calculator</th>
                      <th style={{ padding: '12px 8px', textAlign: 'left' }}>User</th>
                      <th style={{ padding: '12px 8px', textAlign: 'left' }}>Tier</th>
                      <th style={{ padding: '12px 8px', textAlign: 'left' }}>Scenario Name</th>
                      <th style={{ padding: '12px 8px', textAlign: 'left' }}>Created</th>
                      <th style={{ padding: '12px 8px', textAlign: 'left' }}>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scenarios.map((scenario) => (
                      <tr key={scenario.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '12px 8px' }}>{scenario.calculator}</td>
                        <td style={{ padding: '12px 8px' }}>{scenario.user_email}</td>
                        <td style={{ padding: '12px 8px' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            background: scenario.user_tier === 'premium' ? '#ffd700' : '#e0e0e0',
                            color: scenario.user_tier === 'premium' ? '#000' : '#666'
                          }}>
                            {scenario.user_tier}
                          </span>
                        </td>
                        <td style={{ padding: '12px 8px' }}>{scenario.scenario_name}</td>
                        <td style={{ padding: '12px 8px' }}>{new Date(scenario.created_at).toLocaleDateString()}</td>
                        <td style={{ padding: '12px 8px' }}>{new Date(scenario.updated_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Admin Testing Panel - only shows for tylerhargrave@gmail.com */}
          <AdminTestingPanel />
        </>
      )}

      {/* User Management Tab */}
      {activeTab === 'users' && (
        <div className="user-management-section">
          <div className="section-header">
            <h2>👥 User Management</h2>
            <button onClick={fetchUsers} className="refresh-btn">
              🔄 Refresh Users
            </button>
          </div>

          {users.length === 0 ? (
            <div className="no-users-message">
              <p>⚠️ User management requires admin privileges.</p>
              <p>Your account needs to have <code>is_admin = True</code> in the database to access this feature.</p>
            </div>
          ) : (
            <div className="users-table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Auth Method</th>
                    <th>Activated</th>
                    <th>Date Joined</th>
                    <th>Status</th>
                    <th>End Date</th>
                    <th>Payment Amount</th>
                    <th>Payment Date</th>
                    <th>Edit Status</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="user-info-cell">
                        <strong>{user.username}</strong>
                      </div>
                    </td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`auth-method-badge ${user.auth_method}`}>
                        {user.auth_method === 'google' ? '🔗 Google' : '📧 Email'}
                      </span>
                    </td>
                    <td>
                      <span className={`activation-badge ${user.is_verified ? 'verified' : 'unverified'}`}>
                        {user.is_verified ? '✅ Verified' : '❌ Unverified'}
                      </span>
                    </td>
                    <td>{formatDate(user.date_joined)}</td>
                    <td>
                      <span className={`status-badge ${user.subscription_status}`}>
                        {user.subscription_status}
                      </span>
                      <br />
                      <small>Tier: {user.tier}</small>
                    </td>
                    <td>{user.subscription_end_date ? formatDate(user.subscription_end_date) : 'N/A'}</td>
                    <td>{formatCurrency(user.payment_amount)}</td>
                    <td>{user.payment_date ? formatDate(user.payment_date) : 'N/A'}</td>
                    <td>
                      <button
                        className="edit-status-btn"
                        onClick={() => setEditingUser(user)}
                      >
                        ✏️ Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}

          {/* Edit User Status Modal */}
          {editingUser && (
            <div className="modal-overlay">
              <div className="modal-content">
                <h3>Edit User Status: {editingUser.username}</h3>
                <form onSubmit={(e) => {
                  e.preventDefault();

                  const formData = new FormData(e.currentTarget);

                  const updateData = {
                    user_id: editingUser.id,
                    tier: formData.get('tier') as string,
                    subscription_status: formData.get('subscription_status') as string,
                    subscription_end_date: formData.get('subscription_end_date') as string || undefined
                  };

                  updateUserStatus(updateData);
                }}>
                  <div className="form-group">
                    <label htmlFor="tier">Tier:</label>
                    <select name="tier" defaultValue={editingUser.tier}>
                      <option value="free">Free</option>
                      <option value="supporter">Supporter ($10/year)</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="subscription_status">Status:</label>
                    <select name="subscription_status" defaultValue={editingUser.subscription_status}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="expired">Expired</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="subscription_end_date">End Date (Optional):</label>
                    <input 
                      type="date" 
                      name="subscription_end_date"
                      defaultValue={editingUser.subscription_end_date || ''}
                    />
                  </div>
                  
                  <div className="modal-actions">
                    <button 
                      type="button" 
                      onClick={() => setEditingUser(null)}
                      className="cancel-btn"
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="save-btn"
                      disabled={updateLoading}
                    >
                      {updateLoading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Regulatory Updates Tab */}
      {activeTab === 'regulatory' && (
        <div className="regulatory-updates-section">
          <h2>🏛️ Financial Regulatory Updates</h2>
          <p className="regulatory-description">
            Track Canadian financial regulation updates and maintenance tasks for Larry's Help calculators.
            All items are due for review by <strong>January 1, 2026</strong>.
          </p>
          
          <div className="regulatory-grid">
            <div className="regulatory-card">
              <h3>💰 OAS (Old Age Security) Rates</h3>
              <div className="regulatory-status">
                <span className="status-badge due">Due: Jan 1, 2026</span>
                <span className="last-updated">Last Updated: Jan 1, 2025</span>
              </div>
              <ul className="regulatory-tasks">
                <li>Verify current OAS maximum monthly payment</li>
                <li>Check clawback thresholds for 2026</li>
                <li>Update OAS service calculations</li>
                <li>Review quarterly indexation rates</li>
              </ul>
              <div className="regulatory-sources">
                <strong>Sources:</strong> Service Canada, CRA
              </div>
            </div>

            <div className="regulatory-card">
              <h3>🏦 RRIF Withdrawal Rates</h3>
              <div className="regulatory-status">
                <span className="status-badge due">Due: Jan 1, 2026</span>
                <span className="last-updated">Last Updated: Jan 1, 2025</span>
              </div>
              <ul className="regulatory-tasks">
                <li>Verify RRIF withdrawal factors</li>
                <li>Check age-based rate tables</li>
                <li>Update RRIF calculator service</li>
                <li>Review minimum withdrawal rules</li>
              </ul>
              <div className="regulatory-sources">
                <strong>Sources:</strong> CRA, Department of Finance
              </div>
            </div>

            <div className="regulatory-card">
              <h3>📊 CPP (Canada Pension Plan)</h3>
              <div className="regulatory-status">
                <span className="status-badge due">Due: Jan 1, 2026</span>
                <span className="last-updated">Last Updated: Never</span>
              </div>
              <ul className="regulatory-tasks">
                <li>Verify maximum monthly CPP benefit</li>
                <li>Check contribution rates for 2026</li>
                <li>Update pensionable earnings limit</li>
                <li>Implement CPP service if missing</li>
              </ul>
              <div className="regulatory-sources">
                <strong>Sources:</strong> Service Canada, CRA
              </div>
            </div>

            <div className="regulatory-card">
              <h3>💎 TFSA Contribution Limits</h3>
              <div className="regulatory-status">
                <span className="status-badge due">Due: Jan 1, 2026</span>
                <span className="last-updated">Last Updated: Never</span>
              </div>
              <ul className="regulatory-tasks">
                <li>Check 2026 annual contribution limit</li>
                <li>Verify cumulative room calculations</li>
                <li>Update TFSA service if missing</li>
                <li>Review inflation adjustments</li>
              </ul>
              <div className="regulatory-sources">
                <strong>Sources:</strong> CRA, Department of Finance
              </div>
            </div>

            <div className="regulatory-card">
              <h3>🏠 Bank of Canada Rates</h3>
              <div className="regulatory-status">
                <span className="status-badge due">Due: Jan 1, 2026</span>
                <span className="last-updated">Last Updated: Dec 1, 2024</span>
              </div>
              <ul className="regulatory-tasks">
                <li>Update key interest rate</li>
                <li>Check mortgage rate assumptions</li>
                <li>Review housing calculator rates</li>
                <li>Update investment return assumptions</li>
              </ul>
              <div className="regulatory-sources">
                <strong>Sources:</strong> Bank of Canada
              </div>
            </div>

            <div className="regulatory-card">
              <h3>📈 Investment & Tax Rates</h3>
              <div className="regulatory-status">
                <span className="status-badge due">Due: Jan 1, 2026</span>
                <span className="last-updated">Last Updated: Jan 1, 2025</span>
              </div>
              <ul className="regulatory-tasks">
                <li>Verify federal tax brackets</li>
                <li>Check provincial tax rates</li>
                <li>Update investment return assumptions</li>
                <li>Review inflation rate assumptions</li>
              </ul>
              <div className="regulatory-sources">
                <strong>Sources:</strong> CRA, Department of Finance
              </div>
            </div>
          </div>

          <div className="regulatory-actions">
            <h3>🔄 Next Steps</h3>
            <ol>
              <li>Review each regulation category above</li>
              <li>Check official government sources for updates</li>
              <li>Update calculator services with new values</li>
              <li>Test calculations with new scenarios</li>
              <li>Update this list with new due dates</li>
            </ol>
          </div>

          <div className="regulatory-reminder">
            <p><strong>⚠️ Important:</strong> Financial regulations change throughout the year. 
            Check quarterly for OAS updates and monthly for Bank of Canada rate changes.</p>
          </div>
        </div>
      )}

      {/* Admin Panel Tab */}
      {activeTab === 'admin' && (
        <div className="admin-panel-section">
          <AdminPanel user={TylerState.user} />
        </div>
      )}

      <div className="dashboard-footer">
        <button onClick={() => {
          if (activeTab === 'analytics') fetchAnalyticsData();
          else if (activeTab === 'users') fetchUsers();
          // Regulatory tab doesn't need refresh as it's static content
        }} className="refresh-btn">
          🔄 Refresh {activeTab === 'analytics' ? 'Analytics' : activeTab === 'users' ? 'Users' : 'Data'}
        </button>
        <p>Last updated: {new Date().toLocaleString()}</p>
      </div>
    </div>
  );
};

export default AnalyticsDashboard; 